import { type Language, getPageTranslations, getTranslation } from "../lib/translations";
import { Card, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Music, Book, Film, ChevronLeft, ChevronRight } from "lucide-react";
import { useDynamicContent, getCuratedHobbies } from "../lib/dynamicContent";
import { HobbySkeleton } from "./ui/Skeletons";
import { useRef } from "react";

interface HobbyTabProps {
  lang: Language;
}

interface HobbyCategoryColumnProps {
  cat: {
    id: string;
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    items: any[];
    borderColor: string;
    bgColor: string;
    textColor: string;
  };
  lang: string;
}

function HobbyCategoryColumn({ cat, lang }: HobbyCategoryColumnProps) {
  const Icon = cat.icon;
  const hasSlider = cat.items.length > 2;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 260;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="space-y-4 flex flex-col min-w-0">
      {/* Category Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${cat.bgColor} ${cat.textColor}`}>
            <Icon className="h-4 w-4" />
          </div>
          <h5 className="font-bold text-sm text-foreground uppercase tracking-wider">
            {cat.title}
          </h5>
        </div>
        {/* Slider Control Arrows */}
        {hasSlider && (
          <div className="flex gap-1">
            <button
              onClick={() => scroll("left")}
              className="h-6 w-6 rounded-md border border-border/40 flex items-center justify-center hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all"
              aria-label="Previous"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => scroll("right")}
              className="h-6 w-6 rounded-md border border-border/40 flex items-center justify-center hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all"
              aria-label="Next"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Cards Container */}
      <div
        ref={scrollContainerRef}
        className={`flex gap-3 pb-2 scrollbar-none snap-x ${hasSlider
          ? "overflow-x-auto flex-row pr-4"
          : "flex-col overflow-y-visible"
          }`}
        style={{ scrollbarWidth: "none" }}
      >
        {cat.items.map((item) => (
          <Card
            key={item.id}
            className={`border border-border/40 bg-card/45 backdrop-blur-md transition-all duration-300 ${cat.borderColor} hover:shadow-xl hover:shadow-brand-primary/5 hover:-translate-y-0.5 rounded-xl shrink-0 snap-start ${hasSlider ? "w-[240px]" : "w-full"
              }`}
          >
            <CardHeader className="p-4 text-left">
              {item.link ? (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline block"
                >
                  <CardTitle className="text-sm font-extrabold text-foreground group-hover:text-brand-primary transition-colors">
                    {getTranslation(item.title, lang)}
                  </CardTitle>
                </a>
              ) : (
                <CardTitle className="text-sm font-extrabold text-foreground">
                  {getTranslation(item.title, lang)}
                </CardTitle>
              )}
              <CardDescription className="text-xs font-semibold text-muted-foreground mt-1.5 leading-relaxed">
                {getTranslation(item.subtitle, lang)}
              </CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function HobbyTab({ lang }: HobbyTabProps) {
  const t = getPageTranslations(lang).hobby;
  const { hobbies: dynamicHobbies, loading } = useDynamicContent();

  if (loading) {
    return <HobbySkeleton />;
  }

  const allHobbies = getCuratedHobbies(dynamicHobbies);

  // Group by category, slice to max 5 items
  const musicItems = allHobbies.filter((item) => item.category === "music").slice(0, 5);
  const bookItems = allHobbies.filter((item) => item.category === "books").slice(0, 5);
  const filmItems = allHobbies.filter((item) => item.category === "films").slice(0, 5);

  const categories = [
    {
      id: "music",
      title: t.music,
      icon: Music,
      items: musicItems,
      borderColor: "hover:border-brand-primary/40",
      bgColor: "bg-brand-primary/5 dark:bg-brand-primary/10",
      textColor: "text-brand-primary"
    },
    {
      id: "books",
      title: t.books,
      icon: Book,
      items: bookItems,
      borderColor: "hover:border-brand-secondary/40",
      bgColor: "bg-brand-secondary/5 dark:bg-brand-secondary/10",
      textColor: "text-brand-secondary"
    },
    {
      id: "films",
      title: t.films,
      icon: Film,
      items: filmItems,
      borderColor: "hover:border-brand-accent/40",
      bgColor: "bg-brand-accent/5 dark:bg-brand-accent/10",
      textColor: "text-brand-accent"
    }
  ];

  return (
    <div className="space-y-12 max-w-7xl mx-auto py-6 animate-fade-in">
      {/* Curated Favorites Section */}
      <section className="space-y-8">
        <div className="border-b border-border/60 pb-3 text-center md:text-left">
          <h4 className="text-xl font-bold tracking-tight text-foreground">
            {t.curatedTitle}
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5 font-medium">
            {t.curatedSubtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {categories.map((cat) => (
            <HobbyCategoryColumn key={cat.id} cat={cat} lang={lang} />
          ))}
        </div>
      </section>
    </div>
  );
}

export default HobbyTab;
